import * as Y from "yjs"

import { mergeApiHistoryWithClientBuffer } from "@/lib/crdt/client-history-buffer"
import { bytesPreview, yjsPayloadForReplay } from "@/lib/crdt/y-sync-protocol"
import { parseCrdtFilesResponse, parseCrdtHistoryResponse, parseCrdtHistorySnapshotPage } from "@/lib/compiler/guards"
import type { CrdtHistoryDelta, CrdtHistoryResponse, CrdtHistorySnapshot } from "@/lib/compiler/types"
import type {
  CrdtSeedFile,
  HistoryReplayResult,
  HistoryReplayStats,
  HistoryTimelineDateGroup,
  HistoryTimelineEntry,
} from "@/lib/crdt/types"

const DEFAULT_HISTORY_LIMIT = 50
const CRDT_HISTORY_DEBUG =
  typeof process !== "undefined" &&
  process.env.NODE_ENV === "development"

const FORENSIC_TAG = "[CRDT-HISTORY]"
const HISTORY_AUDIT_TAG = "[CRDT:HISTORY]"

function historyAudit(...args: unknown[]): void {
  console.log(HISTORY_AUDIT_TAG, ...args)
}

/** Known no-op encodings from the backend (empty Yjs frames, e.g. sync handshakes). */
const TRIVIAL_BASE64_PATTERNS = /^A+={0,2}$/

type HistoryPayloadVerdict =
  | "ok_raw_yjs"
  | "ok_framed_sync_update"
  | "invalid_base64"
  | "placeholder_aaa"
  | "trivial_zero_bytes"
  | "framed_sync_handshake"
  | "empty_after_unwrap"
  | "apply_threw"

function forensicLog(...args: unknown[]): void {
  if (!CRDT_HISTORY_DEBUG) return
  console.info(FORENSIC_TAG, ...args)
}

function forensicWarn(...args: unknown[]): void {
  if (!CRDT_HISTORY_DEBUG) return
  console.warn(FORENSIC_TAG, ...args)
}

function forensicError(...args: unknown[]): void {
  if (!CRDT_HISTORY_DEBUG) return
  console.error(FORENSIC_TAG, ...args)
}

function hexPreview(bytes: Uint8Array, count = 16): string {
  return Array.from(bytes.slice(0, count))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join(" ")
}

function classifyHistoryUpdateBase64(base64: string): {
  storedBytes: Uint8Array | null
  verdict: HistoryPayloadVerdict
  isAAA: boolean
  hadFraming: boolean
  subType?: number
  yjsBytes: Uint8Array
  note: string
} {
  const trimmed = base64.trim()
  if (!trimmed) {
    return {
      storedBytes: null,
      verdict: "invalid_base64",
      isAAA: false,
      hadFraming: false,
      yjsBytes: new Uint8Array(),
      note: "empty update string from API",
    }
  }

  if (TRIVIAL_BASE64_PATTERNS.test(trimmed)) {
    return {
      storedBytes: new Uint8Array([0, 0]),
      verdict: "placeholder_aaa",
      isAAA: true,
      hadFraming: false,
      yjsBytes: new Uint8Array(),
      note: 'API returned "AAA=" which is base64 for bytes [0x00, 0x00] — NOT a real Yjs edit. Backend must store raw Yjs payload from sync_update.',
    }
  }

  let storedBytes: Uint8Array | null
  try {
    const normalized = trimmed.replace(/-/g, "+").replace(/_/g, "/")
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=")
    const binary = atob(padded)
    storedBytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i += 1) {
      storedBytes[i] = binary.charCodeAt(i)
    }
  } catch {
    return {
      storedBytes: null,
      verdict: "invalid_base64",
      isAAA: false,
      hadFraming: false,
      yjsBytes: new Uint8Array(),
      note: "base64 decode failed",
    }
  }

  if (isTrivialYjsUpdate(storedBytes)) {
    return {
      storedBytes,
      verdict: "trivial_zero_bytes",
      isAAA: storedBytes.byteLength <= 2 && storedBytes.every((b) => b === 0),
      hadFraming: false,
      yjsBytes: storedBytes,
      note: "decoded bytes are all zeros / empty — not a document edit",
    }
  }

  const analysis = yjsPayloadForReplay(storedBytes)
  const yjsBytes = analysis.yjsPayload

  if (analysis.hadFraming && analysis.subType === 2 && yjsBytes.byteLength > 0) {
    return {
      storedBytes,
      verdict: "ok_framed_sync_update",
      isAAA: false,
      hadFraming: true,
      subType: 2,
      yjsBytes,
      note: "WS frame [0][2][payload] — inner payload is valid Yjs (replay unwrap OK). Backend should still persist inner payload only.",
    }
  }

  // Raw Yjs updates can start with bytes that decode as [0][0|1][…]; apply full stored
  // payload (demo-aligned) rather than skipping as a WS handshake frame.
  if (analysis.hadFraming && (analysis.subType === 0 || analysis.subType === 1)) {
    return {
      storedBytes,
      verdict: "ok_raw_yjs",
      isAAA: false,
      hadFraming: false,
      yjsBytes: storedBytes,
      note: "raw Yjs update bytes (leading bytes decode as WS subType 0/1 — apply full stored payload)",
    }
  }

  if (yjsBytes.byteLength === 0) {
    return {
      storedBytes,
      verdict: "empty_after_unwrap",
      isAAA: false,
      hadFraming: false,
      yjsBytes,
      note: "raw bytes present but empty after unwrap",
    }
  }

  return {
    storedBytes,
    verdict: "ok_raw_yjs",
    isAAA: false,
    hadFraming: false,
    yjsBytes,
    note: "raw Yjs update bytes (expected format for history)",
  }
}

type HistoryUpdateClassification = ReturnType<typeof classifyHistoryUpdateBase64>

function decodeHistoryUpdateBase64(base64: string): Uint8Array | null {
  const trimmed = base64.trim()
  if (!trimmed) return null

  try {
    const normalized = trimmed.replace(/-/g, "+").replace(/_/g, "/")
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=")
    const binary = atob(padded)
    const storedBytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i += 1) {
      storedBytes[i] = binary.charCodeAt(i)
    }
    return storedBytes
  } catch {
    return null
  }
}

/**
 * Classify updates from recordClientCrdtDelta — always raw Yjs bytes, never WS-framed.
 * Do not run yjsPayloadForReplay; leading bytes can look like [0][0] handshake by accident.
 */
function classifyClientCapturedUpdateBase64(base64: string): HistoryUpdateClassification {
  const storedBytes = decodeHistoryUpdateBase64(base64)
  if (!storedBytes) {
    return {
      storedBytes: null,
      verdict: "invalid_base64",
      isAAA: false,
      hadFraming: false,
      yjsBytes: new Uint8Array(),
      note: "base64 decode failed",
    }
  }

  if (isTrivialYjsUpdate(storedBytes)) {
    return {
      storedBytes,
      verdict: "trivial_zero_bytes",
      isAAA: storedBytes.byteLength <= 2 && storedBytes.every((b) => b === 0),
      hadFraming: false,
      yjsBytes: storedBytes,
      note: "decoded bytes are all zeros / empty — not a document edit",
    }
  }

  return {
    storedBytes,
    verdict: "ok_raw_yjs",
    isAAA: false,
    hadFraming: false,
    yjsBytes: storedBytes,
    note: "locally captured raw Yjs update (no WS framing unwrap)",
  }
}

function logHistoryApiForensic(history: CrdtHistoryResponse, historyUrl: string): void {
  if (!CRDT_HISTORY_DEBUG) return

  console.group(`${FORENSIC_TAG} crdt-history API response`)
  console.info(`${FORENSIC_TAG} url`, historyUrl)
  console.info(`${FORENSIC_TAG} session_id`, history.session_id)
  console.info(`${FORENSIC_TAG} latest_version`, history.latest_version)
  console.info(`${FORENSIC_TAG} delta_count`, history.deltas.length)

  const uniqueUpdates = new Set(history.deltas.map((d) => d.update))
  console.info(`${FORENSIC_TAG} unique_update_strings`, uniqueUpdates.size)

  if (uniqueUpdates.size === 1 && history.deltas[0]?.update === "AAA=") {
    forensicError(
      "ALL deltas use update=\"AAA=\" — backend is NOT storing real Yjs edits. Fix backend persistence (store raw Yjs bytes from sync_update, not WS envelope or placeholders).",
    )
  }

  history.deltas.forEach((delta) => {
    const c = classifyHistoryUpdateBase64(delta.update)
    console.info(`${FORENSIC_TAG} api_delta`, {
      version: delta.version,
      path: delta.path,
      at: delta.at,
      updateBase64: delta.update,
      updateLen: delta.update.length,
      verdict: c.verdict,
      isAAA: c.isAAA,
      storedLen: c.storedBytes?.byteLength ?? 0,
      storedHex: c.storedBytes ? hexPreview(c.storedBytes) : "",
      storedFirst10: c.storedBytes ? bytesPreview(c.storedBytes, 10) : [],
      yjsLen: c.yjsBytes.byteLength,
      yjsFirst10: bytesPreview(c.yjsBytes, 10),
      note: c.note,
    })
  })

  console.groupEnd()
}

export function isValidHistoryPath(path: string | null | undefined): path is string {
  return typeof path === "string" && path.trim().length > 0
}

/** True when decoded bytes are empty or a trivial Yjs no-op (sync vector / empty update). */
export function isTrivialYjsUpdate(bytes: Uint8Array): boolean {
  if (bytes.byteLength === 0) return true
  if (bytes.byteLength <= 2 && bytes.every((byte) => byte === 0)) return true
  return false
}

/**
 * Decode base64 Yjs update bytes from the history API.
 * Returns null for missing, invalid, or empty/trivial updates (e.g. "AAA=").
 */
export function decodeBase64Update(base64: string): Uint8Array | null {
  const trimmed = base64.trim()
  if (!trimmed || TRIVIAL_BASE64_PATTERNS.test(trimmed)) return null

  try {
    const normalized = trimmed.replace(/-/g, "+").replace(/_/g, "/")
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=")
    const binary = atob(padded)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i)
    }
    return isTrivialYjsUpdate(bytes) ? null : bytes
  } catch {
    return null
  }
}

/**
 * Demo-style base64 decode for replay: only skips missing/invalid base64 (no trivial-byte filter).
 */
function decodeUpdateBase64ForReplay(base64: string): Uint8Array | null {
  const trimmed = base64.trim()
  if (!trimmed) return null

  try {
    const normalized = trimmed.replace(/-/g, "+").replace(/_/g, "/")
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=")
    const binary = atob(padded)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i)
    }
    return bytes
  } catch {
    return null
  }
}

/** Whether a history row carries non-trivial Yjs bytes (may still lack a file path). */
export function hasMeaningfulUpdate(delta: CrdtHistoryDelta): boolean {
  return decodeBase64Update(delta.update) !== null
}

/** Whether a history row should appear on the timeline (path + non-trivial bytes). */
export function isReplayableContentDelta(delta: CrdtHistoryDelta): boolean {
  return isValidHistoryPath(delta.path) && hasMeaningfulUpdate(delta)
}

/** Stats helper: counts pathless / empty rows in raw API payload (replay uses all decodable deltas). */
export function partitionHistoryDeltas(deltas: CrdtHistoryDelta[]): {
  replayable: CrdtHistoryDelta[]
  stats: Pick<
    HistoryReplayStats,
    "totalDeltas" | "skippedEmptyUpdate" | "pathlessUpdates"
  >
} {
  const replayable: CrdtHistoryDelta[] = []
  let skippedEmptyUpdate = 0
  let pathlessUpdates = 0

  for (const delta of deltas) {
    if (!decodeUpdateBase64ForReplay(delta.update)) {
      skippedEmptyUpdate += 1
      continue
    }
    if (!isValidHistoryPath(delta.path)) {
      pathlessUpdates += 1
    }
    replayable.push(delta)
  }

  return {
    replayable,
    stats: {
      totalDeltas: deltas.length,
      skippedEmptyUpdate,
      pathlessUpdates,
    },
  }
}

function collectChangedPaths(
  beforeSnap: Record<string, string>,
  afterSnap: Record<string, string>,
): Set<string> {
  const changedPaths = new Set<string>()
  const allPaths = new Set([...Object.keys(beforeSnap), ...Object.keys(afterSnap)])
  allPaths.forEach((path) => {
    if ((beforeSnap[path] ?? "") !== (afterSnap[path] ?? "")) {
      changedPaths.add(path)
    }
  })
  return changedPaths
}

function pickPreferredFilePath(
  beforeFiles: Record<string, string>,
  afterFiles: Record<string, string>,
  preferredPath?: string,
): string | undefined {
  if (preferredPath && (preferredPath in afterFiles || preferredPath in beforeFiles)) {
    return preferredPath
  }

  const allPaths = new Set([...Object.keys(beforeFiles), ...Object.keys(afterFiles)])
  const texPaths = [...allPaths].filter((path) => path.endsWith(".tex"))
  if (texPaths.length === 1) return texPaths[0]

  const nonAssetPaths = [...allPaths].filter((path) => !path.includes("/assets/"))
  if (nonAssetPaths.length === 1) return nonAssetPaths[0]

  return undefined
}

/** Demo-aligned path resolution: API path → changed path → main/tex file → "(unknown)". */
function resolveReplayPath(
  delta: CrdtHistoryDelta,
  beforeFiles: Record<string, string>,
  afterFiles: Record<string, string>,
  changedPaths: Set<string>,
  preferredPath?: string,
): string {
  if (delta.path && delta.path.trim().length > 0) {
    return delta.path.trim()
  }

  const firstChanged = changedPaths.values().next().value
  if (firstChanged) return firstChanged

  const preferred = pickPreferredFilePath(beforeFiles, afterFiles, preferredPath)
  if (preferred) return preferred

  return (
    Object.keys(afterFiles)[0] ??
    Object.keys(beforeFiles)[0] ??
    "(unknown)"
  )
}

export function summarizeTextChange(before: string, after: string, path: string): string {
  if (before === after) return `No change in ${path}`
  if (before === "" && after !== "") return `Created ${path} (${after.length} chars)`
  if (before !== "" && after === "") return `Removed ${path} (${before.length} chars)`
  const delta = after.length - before.length
  const sign = delta >= 0 ? "+" : ""
  return `Edited ${path} (${sign}${delta} chars)`
}

/** Demo-style snapshot: Y.Text, plain strings, and JSON-serialized map values. */
function readFilesSnapshot(doc: Y.Doc): Record<string, string> {
  const filesMap = doc.getMap<unknown>("files")
  const snapshot: Record<string, string> = {}
  filesMap.forEach((value: unknown, path: string) => {
    if (value instanceof Y.Text) {
      snapshot[path] = value.toString()
      return
    }
    if (typeof value === "string") {
      snapshot[path] = value
      return
    }
    if (value !== null && typeof value === "object") {
      try {
        snapshot[path] = JSON.stringify(value)
        return
      } catch {
        snapshot[path] = ""
        return
      }
    }
    snapshot[path] = ""
  })
  return snapshot
}

function snapshotFiles(doc: Y.Doc): Map<string, string> {
  const record = readFilesSnapshot(doc)
  return new Map(Object.entries(record))
}

/** Seed replay doc with session baseline (template / initial reconstruct) before applying deltas. */
function seedReplayDoc(doc: Y.Doc, seedFiles: CrdtSeedFile[]): void {
  if (seedFiles.length === 0) return

  const filesMap = doc.getMap<Y.Text>("files")
  doc.transact(() => {
    for (const file of seedFiles) {
      const ytext = new Y.Text()
      if (file.content) ytext.insert(0, file.content)
      filesMap.set(file.path, ytext)
    }
  })
}

function historyPageUrl(historyUrl: string, page: number, pageSize: number, projectId?: string): string {
  const url = new URL(historyUrl, typeof window !== "undefined" ? window.location.origin : "http://localhost")
  if (projectId && !url.searchParams.get("project_id")) url.searchParams.set("project_id", projectId)
  if (!url.searchParams.get("after_version")) url.searchParams.set("after_version", "0")
  url.searchParams.set("page", String(page))
  url.searchParams.set("page_size", String(pageSize))
  return url.pathname + url.search
}

function buildHistoryUrl(historyUrl: string, limit: number): string {
  const url = new URL(historyUrl, typeof window !== "undefined" ? window.location.origin : "http://localhost")
  url.searchParams.set("limit", String(limit))
  return url.pathname + url.search
}

const SNAPSHOT_PAGE_SIZE = 100

async function readHistoryJson(url: string): Promise<unknown> {
  const response = await fetch(url, { credentials: "include" })
  const json: unknown = await response.json().catch(() => null)
  if (!response.ok) {
    const message =
      json && typeof json === "object" && "error" in json && typeof (json as { error: unknown }).error === "string"
        ? (json as { error: string }).error
        : `History request failed (${response.status})`
    throw new Error(message)
  }
  return json
}

async function fetchSnapshotHistory(
  historyUrl: string,
  projectId?: string,
): Promise<{ sessionId: string; snapshots: CrdtHistorySnapshot[] } | null> {
  const firstUrl = historyPageUrl(historyUrl, 1, SNAPSHOT_PAGE_SIZE, projectId)
  const firstJson = await readHistoryJson(firstUrl)
  const firstPage = parseCrdtHistorySnapshotPage(firstJson)
  if (!firstPage) return null

  const snapshots = [...firstPage.snapshots]
  for (let page = 2; page <= firstPage.total_pages; page += 1) {
    const nextJson = await readHistoryJson(historyPageUrl(historyUrl, page, SNAPSHOT_PAGE_SIZE, projectId))
    const nextPage = parseCrdtHistorySnapshotPage(nextJson)
    if (!nextPage) break
    snapshots.push(...nextPage.snapshots)
  }

  const seen = new Set<number>()
  const unique = snapshots.filter((snap) => {
    if (seen.has(snap.version)) return false
    seen.add(snap.version)
    return true
  })
  return { sessionId: firstPage.session_id, snapshots: unique }
}

type StructureOp = { type: string; path: string; to: string; file_id: string }

function readStructureOp(item: unknown): StructureOp | null {
  const record: Record<string, unknown> = {}
  if (item instanceof Y.Map) {
    item.forEach((value, key) => {
      record[key] = value
    })
  } else if (item && typeof item === "object") {
    Object.assign(record, item)
  } else {
    return null
  }
  const type = typeof record.type === "string" ? record.type : ""
  if (!type) return null
  return {
    type,
    path: typeof record.path === "string" ? record.path : "",
    to: typeof record.to === "string" ? record.to : "",
    file_id: typeof record.file_id === "string" ? record.file_id : "",
  }
}

function readStructureOps(doc: Y.Doc): StructureOp[] {
  const ops = doc.getArray<unknown>("ops")
  const out: StructureOp[] = []
  ops.forEach((item) => {
    const op = readStructureOp(item)
    if (op) out.push(op)
  })
  return out
}

function pathsFromOps(ops: StructureOp[]): Map<string, string> {
  const paths = new Map<string, string>()
  for (const op of ops) {
    if (!op.file_id) continue
    if (op.type === "delete") paths.delete(op.file_id)
    else if (op.type === "rename" && op.to) paths.set(op.file_id, op.to)
    else if (op.path) paths.set(op.file_id, op.path)
  }
  return paths
}

function readContentById(doc: Y.Doc): Map<string, string> {
  const content = doc.getMap<unknown>("content")
  const out = new Map<string, string>()
  content.forEach((value, key) => {
    if (value instanceof Y.Text) out.set(key, value.toString())
    else if (typeof value === "string") out.set(key, value)
  })
  return out
}

function isLatexText(text: string): boolean {
  return text.includes("\\documentclass") || text.includes("\\begin{document}")
}

function textsByPath(
  byId: Map<string, string>,
  idToPath: Map<string, string>,
  preferredPath?: string,
): Record<string, string> {
  const out: Record<string, string> = {}
  const unresolvedLatex: string[] = []
  byId.forEach((text, id) => {
    const mapped = idToPath.get(id)
    if (mapped) {
      out[mapped] = text
      return
    }
    if (id.includes("/") || id.includes(".")) {
      out[id] = text
      return
    }
    if (isLatexText(text)) unresolvedLatex.push(text)
  })
  if (unresolvedLatex.length === 1 && preferredPath && !(preferredPath in out)) {
    out[preferredPath] = unresolvedLatex[0]
  }
  return out
}

function pickChangedPath(
  before: Record<string, string>,
  after: Record<string, string>,
  preferredPath?: string,
): string | null {
  const paths = new Set([...Object.keys(before), ...Object.keys(after)])
  let best: { path: string; score: number } | null = null
  for (const path of paths) {
    if ((before[path] ?? "") === (after[path] ?? "")) continue
    if (path.endsWith("project.rudra.config.json")) continue
    const delta = Math.abs((after[path] ?? "").length - (before[path] ?? "").length)
    const score = delta + (path === preferredPath ? 1_000_000 : 0) + (path.endsWith(".tex") ? 10_000 : 0)
    if (!best || score > best.score) best = { path, score }
  }
  return best?.path ?? null
}

function describeStructureOp(op: StructureOp): { path: string; description: string } | null {
  if (op.type === "rename" && op.path && op.to) {
    return { path: op.to, description: `Renamed ${op.path} → ${op.to}` }
  }
  if (op.type === "create" && op.path) return { path: op.path, description: `Created ${op.path}` }
  if (op.type === "delete" && op.path) return { path: op.path, description: `Deleted ${op.path}` }
  if (op.type === "asset" && op.path) return { path: op.path, description: `Added ${op.path}` }
  return null
}

function replaySnapshotHistory(
  sessionId: string,
  snapshots: CrdtHistorySnapshot[],
  options?: {
    preferredPath?: string
    onProgress?: (current: number, total: number) => void
  },
): HistoryReplayResult {
  const sorted = [...snapshots].sort((a, b) => a.version - b.version)
  const doc = new Y.Doc()
  const entries: HistoryTimelineEntry[] = []
  let prevTexts: Record<string, string> = {}
  let prevOps: StructureOp[] = []
  let applied = 0
  let skippedEmptyUpdate = 0
  let skippedNoContentChange = 0

  try {
    for (let i = 0; i < sorted.length; i += 1) {
      const snap = sorted[i]
      const bytes = decodeUpdateBase64ForReplay(snap.state)
      if (!bytes) {
        skippedEmptyUpdate += 1
        options?.onProgress?.(i + 1, sorted.length)
        continue
      }
      try {
        Y.applyUpdate(doc, bytes)
        applied += 1
      } catch {
        skippedEmptyUpdate += 1
        options?.onProgress?.(i + 1, sorted.length)
        continue
      }

      const ops = readStructureOps(doc)
      const texts = textsByPath(readContentById(doc), pathsFromOps(ops), options?.preferredPath)
      const changedPath = pickChangedPath(prevTexts, texts, options?.preferredPath)
      const structural = [...ops.slice(prevOps.length)].reverse().map(describeStructureOp).find((item) => item != null) ?? null

      if (changedPath) {
        const before = prevTexts[changedPath] ?? ""
        const after = texts[changedPath] ?? ""
        entries.push({
          id: `v${snap.version}-${changedPath}-${snap.at}`,
          version: snap.version,
          sessionId: snap.session_id || sessionId,
          path: changedPath,
          at: snap.at,
          before,
          after,
          description: summarizeTextChange(before, after, changedPath),
          userName: snap.user_name || undefined,
        })
      } else if (structural) {
        entries.push({
          id: `v${snap.version}-${structural.path}-${snap.at}`,
          version: snap.version,
          sessionId: snap.session_id || sessionId,
          path: structural.path,
          at: snap.at,
          before: prevTexts[structural.path] ?? "",
          after: texts[structural.path] ?? prevTexts[structural.path] ?? "",
          description: structural.description,
          userName: snap.user_name || undefined,
        })
      } else {
        skippedNoContentChange += 1
      }

      prevTexts = texts
      prevOps = ops
      options?.onProgress?.(i + 1, sorted.length)
    }

    return {
      sessionId,
      latestVersion: sorted[sorted.length - 1]?.version ?? 0,
      entries,
      finalFiles: new Map(Object.entries(prevTexts)),
      stats: {
        totalDeltas: sorted.length,
        skippedEmptyUpdate,
        pathlessUpdates: 0,
        skippedNoContentChange,
        applied,
        timelineEntries: entries.length,
      },
    }
  } finally {
    doc.destroy()
  }
}

export async function fetchHistory(
  historyUrl: string,
  limit = DEFAULT_HISTORY_LIMIT,
): Promise<CrdtHistoryResponse> {
  const fullUrl = buildHistoryUrl(historyUrl, limit)
  historyAudit("history API fetch", { endpoint: fullUrl, limit })
  const response = await fetch(fullUrl, { credentials: "include" })
  const json: unknown = await response.json().catch(() => null)
  if (!response.ok) {
    const message =
      json && typeof json === "object" && "error" in json && typeof (json as { error: unknown }).error === "string"
        ? (json as { error: string }).error
        : `History request failed (${response.status})`
    throw new Error(message)
  }
  const parsed = parseCrdtHistoryResponse(json)
  if (!parsed) {
    throw new Error("Invalid CRDT history payload")
  }
  historyAudit("history API response", {
    endpoint: fullUrl,
    session_id: parsed.session_id,
    delta_count: parsed.deltas.length,
    latest_version: parsed.latest_version,
    deltas: parsed.deltas.map((d) => ({
      version: d.version,
      path: d.path,
      updateBase64: d.update,
      updateLen: d.update.length,
      at: d.at,
    })),
  })
  logHistoryApiForensic(parsed, fullUrl)
  return parsed
}

function entryIdFor(delta: CrdtHistoryDelta, path: string): string {
  return `v${delta.version}-${path}-${delta.at}`
}

function buildTimelineDescription(
  path: string,
  before: string,
  after: string,
  applyError: string | undefined,
  verdict: HistoryPayloadVerdict | undefined,
): string {
  if (applyError) {
    return `Replay error: ${applyError}`
  }
  if (verdict === "placeholder_aaa") {
    return "Invalid history: backend returned AAA= (empty bytes, not a real edit)"
  }
  if (verdict === "trivial_zero_bytes") {
    return "Invalid history: zero-byte update (backend did not persist Yjs edit)"
  }
  if (verdict === "framed_sync_handshake") {
    return "Skipped: sync handshake frame stored as history (not an edit)"
  }
  if (verdict === "empty_after_unwrap") {
    return "Empty Yjs payload after unwrapping WS frame"
  }
  return summarizeTextChange(before, after, path)
}

function buildTimelineEntry(
  delta: CrdtHistoryDelta,
  path: string,
  before: string,
  after: string,
  applyError?: string,
  verdict?: HistoryPayloadVerdict,
): HistoryTimelineEntry {
  return {
    id: entryIdFor(delta, path),
    version: delta.version,
    sessionId: delta.session_id,
    path,
    at: delta.at,
    before,
    after,
    description: buildTimelineDescription(path, before, after, applyError, verdict),
  }
}

function logReplayDelta(debug: {
  version: number
  apiPath: string | null
  updateBase64: string
  verdict: HistoryPayloadVerdict
  resolvedPath: string
  beforeLen: number
  afterLen: number
  filesMapKeysBefore: string[]
  filesMapKeysAfter: string[]
  changedPaths: string[]
  identicalBeforeAfter: boolean
  applyError?: string
  storedFirst10: number[]
  yjsFirst10: number[]
}): void {
  if (!CRDT_HISTORY_DEBUG) return

  const line = {
    version: debug.version,
    apiPath: debug.apiPath,
    updateBase64: debug.updateBase64,
    payloadVerdict: debug.verdict,
    resolvedPath: debug.resolvedPath,
    beforeLen: debug.beforeLen,
    afterLen: debug.afterLen,
    filesBefore: debug.filesMapKeysBefore,
    filesAfter: debug.filesMapKeysAfter,
    changedPaths: debug.changedPaths,
    applyError: debug.applyError,
    storedFirst10: debug.storedFirst10,
    yjsFirst10: debug.yjsFirst10,
  }

  const bad =
    debug.verdict === "placeholder_aaa" ||
    debug.verdict === "trivial_zero_bytes" ||
    debug.verdict === "framed_sync_handshake" ||
    Boolean(debug.applyError) ||
    (debug.filesMapKeysAfter.length === 0 && debug.version === 1)

  if (bad) forensicWarn("replay_delta", line)
  else forensicLog("replay_delta", line)
}

function logReplayStats(
  stats: HistoryReplayStats,
  counts: {
    framedPayloadsStripped: number
    rawPayloads: number
    okPayloads: number
    aaaCount: number
  },
): void {
  if (!CRDT_HISTORY_DEBUG) return

  const payload = {
    ...stats,
    framedPayloadsStripped: counts.framedPayloadsStripped,
    rawPayloads: counts.rawPayloads,
    okPayloads: counts.okPayloads,
    aaaCount: counts.aaaCount,
  }

  if (counts.aaaCount > 0 || counts.okPayloads === 0) {
    forensicError("replay_summary — history cannot show real diffs", payload)
    forensicError(
      "Compare with live WS: [CRDT WS] outgoing raw yjs payload first10 should match history yjsFirst10 after unwrap. If live shows [1,1,…] but history shows [0,0] or [0,2,…], backend persistence is wrong.",
    )
  } else {
    forensicLog("replay_summary", payload)
  }
}

/**
 * Replay deltas on a temporary Y.Doc (demo-aligned).
 * One timeline row per API delta; permissive path fallback; rows kept even when before === after.
 */
export async function replayHistory(
  historyUrl: string,
  options?: {
    limit?: number
    /** Session baseline (template at connect time) so incremental history deltas can apply. */
    seedFiles?: CrdtSeedFile[]
    /** Prefer this path when API path is null (e.g. main .tex file). */
    preferredPath?: string
    /** Required by the snapshot history API. */
    projectId?: string
    onProgress?: (current: number, total: number) => void
  },
): Promise<HistoryReplayResult> {
  const limit = options?.limit ?? DEFAULT_HISTORY_LIMIT
  const snapshots = await fetchSnapshotHistory(historyUrl, options?.projectId)
  if (snapshots) {
    return replaySnapshotHistory(snapshots.sessionId, snapshots.snapshots, options)
  }
  const fetched = await fetchHistory(historyUrl, limit)
  const history = mergeApiHistoryWithClientBuffer(fetched)
  if (history.usedClientFallback && CRDT_HISTORY_DEBUG) {
    forensicWarn(
      "API history was all AAA= — replaying from locally captured sync_update bytes (backend persistence still broken).",
      { clientDeltaCount: history.deltas.length },
    )
  }
  const sorted = [...history.deltas].sort((a, b) => a.version - b.version)

  historyAudit("replay start", {
    endpoint: historyUrl,
    session_id: history.session_id,
    delta_count: sorted.length,
    latest_version: history.latest_version,
    usedClientFallback: history.usedClientFallback,
    seedFileCount: options?.seedFiles?.length ?? 0,
  })

  const replayDoc = new Y.Doc()
  if (options?.seedFiles && options.seedFiles.length > 0) {
    seedReplayDoc(replayDoc, options.seedFiles)
    forensicLog("replay seeded baseline files", {
      count: options.seedFiles.length,
      paths: options.seedFiles.map((f) => f.path),
    })
  }
  const entries: HistoryTimelineEntry[] = []
  let skippedEmptyUpdate = 0
  let pathlessUpdates = 0
  let skippedNoContentChange = 0
  let applied = 0
  let framedPayloadsStripped = 0
  let rawPayloads = 0
  let okPayloads = 0
  let aaaCount = 0

  const classifyUpdate = history.usedClientFallback
    ? classifyClientCapturedUpdateBase64
    : classifyHistoryUpdateBase64

  try {
    for (let i = 0; i < sorted.length; i += 1) {
      const delta = sorted[i]
      const classification = classifyUpdate(delta.update)
      const { storedBytes, verdict, yjsBytes, hadFraming, note } = classification
      const beforeFiles = readFilesSnapshot(replayDoc)
      let applyError: string | undefined

      let replayOutcome: "applied" | "skipped" = "skipped"
      let replayReason = note

      if (verdict === "placeholder_aaa") {
        aaaCount += 1
        skippedEmptyUpdate += 1
        applyError = note
        replayReason = note
      } else if (verdict === "invalid_base64") {
        skippedEmptyUpdate += 1
        applyError = note
        replayReason = note
      } else if (verdict === "trivial_zero_bytes") {
        skippedEmptyUpdate += 1
        applyError = note
        replayReason = note
      } else if (verdict === "empty_after_unwrap") {
        if (hadFraming) framedPayloadsStripped += 1
        skippedNoContentChange += 1
        applyError = note
        replayReason = note
      } else if (verdict === "ok_framed_sync_update" || verdict === "ok_raw_yjs") {
        okPayloads += 1
        if (hadFraming) framedPayloadsStripped += 1
        else rawPayloads += 1

        try {
          Y.applyUpdate(replayDoc, yjsBytes)
          applied += 1
          replayOutcome = "applied"
          replayReason = hadFraming ? "ok_framed_sync_update_unwrapped" : "ok_raw_yjs"
        } catch (error) {
          applyError =
            error instanceof Error ? error.message : "Failed to decode/apply CRDT update."
          skippedNoContentChange += 1
          replayReason = applyError
        }
      }

      if (!isValidHistoryPath(delta.path)) {
        pathlessUpdates += 1
      }

      const afterFiles = readFilesSnapshot(replayDoc)
      const changedPaths = collectChangedPaths(beforeFiles, afterFiles)
      const resolvedPath = resolveReplayPath(
        delta,
        beforeFiles,
        afterFiles,
        changedPaths,
        options?.preferredPath,
      )

      historyAudit("replay delta", {
        version: delta.version,
        path: resolvedPath,
        apiPath: delta.path,
        outcome: replayOutcome,
        reason: replayReason,
        verdict,
        applyError,
      })
      const before = beforeFiles[resolvedPath] ?? ""
      const after = afterFiles[resolvedPath] ?? before
      const identicalBeforeAfter = before === after

      if (identicalBeforeAfter && !applyError) {
        skippedNoContentChange += 1
      }

      entries.push(
        buildTimelineEntry(delta, resolvedPath, before, after, applyError, verdict),
      )

      logReplayDelta({
        version: delta.version,
        apiPath: delta.path,
        updateBase64: delta.update,
        verdict,
        resolvedPath,
        beforeLen: before.length,
        afterLen: after.length,
        filesMapKeysBefore: Object.keys(beforeFiles),
        filesMapKeysAfter: Object.keys(afterFiles),
        changedPaths: [...changedPaths],
        identicalBeforeAfter,
        applyError,
        storedFirst10: storedBytes ? bytesPreview(storedBytes, 10) : [],
        yjsFirst10: bytesPreview(yjsBytes, 10),
      })

      options?.onProgress?.(i + 1, sorted.length)
    }

    const stats: HistoryReplayStats = {
      totalDeltas: sorted.length,
      skippedEmptyUpdate,
      pathlessUpdates,
      skippedNoContentChange,
      applied,
      timelineEntries: entries.length,
    }
    logReplayStats(stats, { framedPayloadsStripped, rawPayloads, okPayloads, aaaCount })

    historyAudit("replay complete", {
      session_id: history.session_id,
      timelineEntries: entries.length,
      stats,
    })

    return {
      sessionId: history.session_id,
      latestVersion: history.latest_version,
      entries,
      finalFiles: snapshotFiles(replayDoc),
      stats,
      usedClientFallback: history.usedClientFallback,
    }
  } finally {
    replayDoc.destroy()
  }
}

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
})

const timeFormatter = new Intl.DateTimeFormat(undefined, {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
})

function formatEntryDate(iso: string): { date: string; time: string } {
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) {
    return { date: "Unknown date", time: iso }
  }
  return {
    date: dateFormatter.format(parsed),
    time: timeFormatter.format(parsed),
  }
}

/** Group timeline entries by calendar date (newest dates and versions first). */
export function groupHistoryEntriesByDate(entries: HistoryTimelineEntry[]): HistoryTimelineDateGroup[] {
  const byDate = new Map<string, { at: string; entries: HistoryTimelineEntry[] }>()

  for (const entry of entries) {
    const { date } = formatEntryDate(entry.at)
    const list = byDate.get(date)
    if (list) list.entries.push(entry)
    else byDate.set(date, { at: entry.at, entries: [entry] })
  }

  return Array.from(byDate.entries())
    .map(([date, group]) => ({
      date,
      at: group.at,
      entries: [...group.entries].sort((a, b) => b.version - a.version || b.at.localeCompare(a.at)),
    }))
    .sort((a, b) => Date.parse(b.at) - Date.parse(a.at))
    .map(({ date, entries: groupEntries }) => ({
      date,
      entries: groupEntries,
    }))
}

/** Fetch reconstruct snapshot and return seed files (empty array if unavailable). */
export async function fetchReconstructSeedFiles(
  reconstructUrl: string,
): Promise<CrdtSeedFile[]> {
  historyAudit("reconstruct fetch (shared helper)", { endpoint: reconstructUrl })
  const response = await fetch(reconstructUrl, { credentials: "include" })
  if (!response.ok) {
    historyAudit("reconstruct response", {
      endpoint: reconstructUrl,
      ok: false,
      status: response.status,
    })
    return []
  }

  const json: unknown = await response.json().catch(() => null)
  const parsed = parseCrdtFilesResponse(json)
  if (!parsed?.files) {
    historyAudit("reconstruct response", {
      endpoint: reconstructUrl,
      ok: true,
      valid: false,
      shape: json && typeof json === "object" ? Object.keys(json as object) : typeof json,
    })
    return []
  }

  const files = Object.entries(parsed.files).map(([path, content]) => ({ path, content }))
  historyAudit("reconstruct response", {
    endpoint: reconstructUrl,
    ok: true,
    session_id: parsed.session_id,
    responseShape: {
      fileCount: files.length,
      paths: files.map((f) => f.path),
    },
  })
  return files
}
